import { Injectable } from '@angular/core';
import { DataContextService } from './data-context.service';
import { CoresService } from './cores.service';
import { Observable, catchError, forkJoin, map, of, tap, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Org } from '../models/plugin-config';
import { size } from 'lodash';
import { PapiService } from './papi.service';
import { DataService } from './data.service';
import { GlobalSettingsService } from './global-settings.service';
import { PerimeterMapEntity } from './PerimeterMapEntity.service';
import _ from 'lodash';
import { OrganizationUtilService } from './organization-util.service';
import { CoreService } from './core.service';
import { CameraService } from './camera.service';
import { WindowService } from './window.service';
import { ConfigurationManagerService } from './configuration-manager.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TimelineService } from './timeline.service';

@Injectable({
  providedIn: 'root'
})
export class ArcAngelPapiService {
  private _arcliveActions: string[] | null = null;
  private papiCallCounter = 0;
  data  = {
    myOrgs: {} as Record<string, any>,
    myBeaconsById: {} as Record<string, any>,
    myBeacons: {} as Record<string, any>,
    myBuildings: {} as Record<string, any>
  };

  private foreverEntitiesAdded = {
    perimeters: {},
    facilities: {},
    remotes: {},
    areas: {},
  };

  private _canCreateIncidents = false;
  private _managingOrgId: any;

  

  constructor(
    private http: HttpClient,
    private papiService: PapiService,
    private dataService: DataService,
    // private dataService: DataService,
     //private papiAuth: PapiAuthService,
    // private papi: Papi,
    // private environment: Environment,
    // private sensor: Sensor,
    // private organization: Organization,
    private dataContext: DataContextService,
    // private perimeterMapEntity: PerimeterMapEntity,
    // private buildingMapEntity: BuildingMapEntity,
    // private areaMapEntity: AreaMapEntity,
    // private userService: any, // <-- Add this line (replace 'any' with actual UserService type if available)
    // private sessionOptionsService: SessionOptionsService,
    // private feeds: Feeds,
    // private fmvFeed: FmvFeed,
    private windowService: WindowService,
    // private pinActivationDialogOptions: PinActivationDialogOptions,
    // private buildingTiles: BuildingTiles,
    // private facilityPinMapEntity: FacilityPinMapEntity,
    // private remotePinMapEntity: RemotePinMapEntity,
    // private arcAngelSocialMediaService: ArcAngelSocialMediaService,
    private globalSettings: GlobalSettingsService,
    private cores: CoresService,
    private cor : CoreService,
    private orgUtilService: OrganizationUtilService,
    private cameraService: CameraService,
    private dataContextService: DataContextService,
    private modalService: NgbModal,
    private timelineService: TimelineService
  ) {}

  resetManagingOrgId(): void {
    this._managingOrgId = undefined;
  }

  setManagingOrgId(n: any): void {
    this._managingOrgId = n;
  }

  getData() {
    return this.data;
  }

  assignData(value: any) {
    if (value) {
      Object.assign(this.data, value);
    }
    return this.data;
  }

  canCreateIncident(): boolean {
    return this._canCreateIncidents;
  }

  startForeverPoller(): Promise<any> {
    const params = {
      body: {
        where: {
          $or: [
            { code: 'papi.db.Incident.createGeofence' },
            { code: 'papi.db.Incident.createGeofencePublic' },
          ],
        },
      },
    };

    const calls = {
      // canCreateIncidents: this.papi.request(
      //   'foreverPoller-getActionSessionWithOrganizationIds' +
      //     this.papiCallCounter++,
      //   'db',
      //   'getActionSessionWithOrganizationIds',
      //   params
      // ),
    };

    return forkJoin(calls)
      .toPromise()
      .then((results) => {
        // const profile = this.papiAuth.profile;
        // this.data.myName = profile.name;
        // this.data.myId = profile.id;
        // this.data.myEmail =
        //   profile.UserEmails && profile.UserEmails.length > 0
        //     ? profile.UserEmails[0].address
        //     : '';
        // this.data.myUserName = profile.username;

        // if (results.canCreateIncidents.data.rows.length > 0) {
        //   this._canCreateIncidents = true;
        // }

        return this.loadBuildingsAndOrganizations();
      })
      .catch((error) => {
        console.error('Error retrieving profile data', error);
        throw error;
      });
  }

  private async loadBuildingsAndOrganizations(): Promise<any> {
    try {
      // First load buildings
      const buildingsResponse = await this.getBuildings().toPromise();
      if (buildingsResponse) {
        this.data.myBuildings = {};
        for (const building of buildingsResponse) {
          const buildingEntity = new PerimeterMapEntity(
            building.id,
            building.label,
            flipLocations(building.geometry.coordinates[0]),
            building.createdAt,
            building.organizationId,
            0,
            0,
            null,
            'BUILDING',
            building.ancestors,
            '',
            this.cameraService,
            building.attributes || {},
            this.cores,
            this.cor,
            this.windowService,
            this.dataContextService,
            this.modalService,
            this.timelineService
          );
          
          this.dataContext.addMapEntity(buildingEntity);
          this.data.myBuildings[building.id] = buildingEntity;
        }
      }

      // Then load organizations
      return this.organizationsPoller();
    } catch (error) {
      console.error('Error loading buildings and organizations:', error);
      throw error;
    }
  }

  async organizationsPoller(): Promise<any> {
    this.data.myOrgs = {}; // reset
    try {
      const myData = await this.loadOrgData();

      // Process geofences and org objects
      for (const orgData of Object.values(this.data.myOrgs)) {
        await this.getOrgObjects(orgData); // Wait for this async call
      }

      // Add hierarchical label to myOrgs
      await this.loadOrgLabels();
      // Call setPerimeters with the organizations
      const perimeterResults = await this.setPerimeters(Object.values(this.data.myOrgs));

    } catch (error) {
      console.error('Error retrieving organizations', error);
      throw error;
    }
  }

  private async getOrgObjects(org: any): Promise<any> {
    // Remove references to groups, departments, and business units
    let removeRefsToThis: any[] = [];
    org.setGroups(removeRefsToThis);
    org.setDepartments(removeRefsToThis);
    org.setUnits(removeRefsToThis);

    // Prepare location data for remotes and buildings
    const addresses : any[] = [];
    // const locationData = this.transforms.remotes(addresses);
    // const buildingLocations = this.transforms.buildingPointLocations(org.perimeters, org.getId());
   // org.setEntityLocations([...locationData, ...buildingLocations]);

    // Process each entity location
    // for (const entityLocation of org.entityLocations) {
    //   if (entityLocation.locationType === 'facility') {

    //     if (entityLocation.geometry && entityLocation.geometry.coordinates) {
    //       const facilityPin = new FacilityPinMapEntity(
    //         entityLocation.label,
    //         entityLocation.geometry.coordinates[1],
    //         entityLocation.geometry.coordinates[0],
    //         entityLocation
    //       );

    //       if (entityLocation.attributes && entityLocation.attributes.bluetooth) {
    //         this.data.myBluetoothFacilityPins[entityLocation.id] = facilityPin;
    //       }

    //       this.data.myFacilityPins[entityLocation.id] = facilityPin;

    //       const perimeter = this.findPerimeter(this.data.myOrgs, entityLocation.attributes.basePerimeterId);
    //       const newPerimeterEntity = new PerimeterMapEntity(
    //         perimeter.id,
    //         perimeter.label,
    //         this.flipLocations(perimeter.geometry.coordinates[0]),
    //         perimeter.updatedAt,
    //         org.getId(),
    //         0,
    //         0,
    //         null,
    //         perimeter.type,
    //         perimeter.ancestors
    //       );

    //       this.data.myPerimeters[perimeter.id] = newPerimeterEntity;
    //       this.foreverEntitiesAdded.perimeters[perimeter.id] = newPerimeterEntity;
    //     } else {
    //     }
    //   } else if (entityLocation.locationType === 'remote') {
    //     if (entityLocation.attributes.userId) {
    //       const remotePin = new RemotePinMapEntity(
    //         entityLocation.label,
    //         entityLocation.geometry.coordinates[1],
    //         entityLocation.geometry.coordinates[0],
    //         entityLocation
    //       );

    //       this.data.myRemotePins[entityLocation.id] = remotePin;
    //       this.dataContext.addMapEntity(remotePin);
    //     } else {
    //     }
    //   }
    // }

    return org;
  }

 
async setPerimeters(orgs: any[]) {
  const perimeterResults: any[] = [];
 _.forEach(orgs, (org: any) => {
    org.perimeters.forEach((perimeter: any) => {
      if (perimeter.type === 'FLOOR' || perimeter.type === 'ROOM') {

        const floor = perimeter.type === 'FLOOR'
          ? perimeter.floor
          : this.findPerimeter(org.myOrgs, perimeter.parentId)?.floor;

        let elevation = floor ? floor.elevation + 1.5 : 1.5;

        if (perimeter.floor?.parentPerimeterId) {
          elevation += 0.5;
        }

        const alt = elevation + 0.5;
        try{
          const mapPerimeter = new PerimeterMapEntity(
            perimeter.id,
            perimeter.label,
            flipLocations(perimeter.geometry.coordinates[0]),
            perimeter.updatedAt,
            org.id,
            alt,
            elevation, // Assuming a default height, adjust as needed
            perimeter.floor?.id || null,
            perimeter.type,
            perimeter.ancestors,
            '',
            this.cameraService,
            perimeter.attributes || {},
            this.cores,
            this.cor,
            this.windowService,
            this.dataContextService,
            this.modalService,
            this.timelineService
          );

        this.dataContext.addMapEntitytest(mapPerimeter);
        perimeterResults.push(mapPerimeter);
      }
      catch(error) {
        console.error('Error processing perimeter:', error);
      }
    }
    });
  });
  return perimeterResults;
}

 findPerimeter(orgs: any, perimeterId: string): any | null {
    if (!orgs || !perimeterId) return null;

    const orgList = Array.isArray(orgs) ? orgs : Object.values(orgs);

    for (const org of orgList) {
      if (!org.perimeters) continue;

      for (const perimeter of org.perimeters) {
        if (perimeter.id === perimeterId) {
          return perimeter;
        }
      }
    }

    return null;
  }



  //--------------------------------------------------------
  loadOrgData(): Observable<any> {
    // Common parameters used for calls to PAPI
    const params = { body: { order: [['id', 'DESC']] } };


    const calls = {
      orgs: this.getOrganizations(),
      orgPocs: this.request('loadOrgData-findOrganizationContact'),
      group: this.request('loadOrgData-findGroup'),
      perimeters: this.request('loadOrgData-findPerimeter'),
      perimeterPocs: this.request('loadOrgData-findPerimeterContact'),
      orgPerimeters: this.request('loadOrgData-findOrganizationPerimeter'),
      perimeterSafezones: this.request('loadOrgData-findPerimeterSafezone'),
      beacons: this.request('loadOrgData-findBeacon'),
      buildings: this.request('loadOrgData-findBuilding'),
      floors: this.request('loadOrgData-findFloor'),
      floorplans: this.request('loadOrgData-findFloorplan'),
      sops: this.request('loadOrgData-findSop'),
      sopSteps: this.request('loadOrgData-findSopStep', { body: { order: [['order', 'ASC']] } }),
      incidentCategories: this.request('loadOrgData-findIncidentCategory', { body: { order: [['label', 'ASC']] } }),
      organizationIncidentCategories: this.request('loadOrgData-findOrganizationIncidentCategory')
    };

    // Return an Observable to handle all requests
    return forkJoin(calls).pipe(
      map((results: any) => {
        let userProfile = this.getUserProfile();

        // Transform the results from the new PAPI calls into the original Tincan data format
        let mydata = this.createMyData(results, userProfile);

        // Process perimeters and other related data
        mydata.myOrgs = this.processOrganizations(results, mydata);
        // Return the processed data
        return mydata;
      }),
      catchError((error) => {
        console.error('Error retrieving profile and organization.', error);
        throw new Error('Error retrieving profile and organization');
      })
    );
  }
setBuildingIncident(incident: any): void {
  for (const building of this.orgUtilService.data.myBuildings) {
    if (building.isIncidentABuildingIncident(incident)) {
      building.addIncident(incident);
    }
  }
}

  // Simulate an API request, replace with actual implementation
  request(endpoint: string, params: any = {}): Observable<any> {
    // Simulate an HTTP request (replace with actual HTTP calls)
    return this.http.post<any>(`/api/${endpoint}`, params).pipe(
      catchError((err) => {
        console.error(`Error in ${endpoint}:`, err);
        throw err;
      })
    );
  }

  // Helper method to simulate retrieving user profile
  getUserProfile(userId?: any): any {
    const url = `${ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.Profile}/${userId}/profile/`;
    return this.http.get<any>(url);
  }

  // Helper method to simulate organization retrieval
  getOrganizations() {
    return this.http.get('/api/organizations'); // Example API call
  }

  // Create myData from the API results
  private createMyData(results: any, userProfile: any) {
    let mydata = {
      myName: userProfile.name,
      myId: userProfile.id,
      myPermissions: {}, // No longer used
      myOrgs: {},
      myOrgIds: results.orgs.map((org: any) => org.id),
      papiPerimeters: {}
    };

    // Further processing and mapping would happen here
    return mydata;
  }

  // Process organizations and map additional data
  private processOrganizations(results: any, mydata: any) {
    results.orgs.forEach((orgData: any) => {
      const safezones: any[] = [];
      const facilities: any[] = [];
      let perimeters: any[] = [];
      let perimeterIds: any[] = [];
      let buildings: any[] = [];

      // Process buildings
      if (results.buildings) {
        buildings = results.buildings.filter((building: any) => 
          building.organizationId === orgData.id
        );
      }

      // Create new organization object with related data
      let org = {
        ...orgData,
        safezones,
        facilities,
        perimeters,
        perimeterIds,
        buildings
      };

      mydata.myOrgs[orgData.id] = org;
    });

    return mydata.myOrgs;
  }
  loadOrgLabels(): void {
    // Add labels to orgs
    Object.entries(this.data.myOrgs).forEach(([key, org]) => {
      this.data.myOrgs[key] = {
        ...org,
        label: this.buildAncestorLabel(org),
      };
    });

    // Remove 'Public' orgs
    Object.entries(this.data.myOrgs).forEach(([key, org]) => {
      if (org.label === 'Public') {
        delete this.data.myOrgs[key];
      }
    });
  }
// setBuildingIncident(incident: any): void {
//   for (const building of data.myBuildings) {
//     if (building.isIncidentABuildingIncident(incident)) {
//       building.addIncident(incident);
//     }
//   }
// }
  private buildAncestorLabel(org: Org): string {
    // Replace this with your actual logic
    return org?.name ?? 'Unknown Org';
  }

  public readonly ArcliveActions = {
    arclive: 'arclive',
    arclive_integration_genetec: 'arclive.integration.genetec',
    arclive_integration_socialMedia: 'arclive.integration.socialMedia',
    arclive_feature_showHomeLocations: 'arclive.feature.showHomeLocations',
    arclive_communication_sms: 'arclive.communication.sms',
    arclive_communication_notification: 'arclive.communication.notification',
    arclive_communication_email: 'arclive.communication.email',
    arclive_communication_voice: 'arclive.communication.voice',
  };
  
  getArcliveActions(): Observable<string[]> {
    // Return cached list if already fetched
    if (this._arcliveActions) {
      return of(this._arcliveActions);
    }

    const params = {
      body: {
        attributes: ['code', 'id'],
        where: {
          $or: [
            { code: { $like: 'arclive%' } },
            { code: { $like: 'analytics' } }
          ]
        }
      }
    };

    const papiMethod = 'getOrgSettings-getActionSessionWithOrganizationIds' + this.papiCallCounter++;
    const dbMethod = 'getActionSessionWithOrganizationIds';

    return this.papiService.request('papiMethod', 'db', 'dbMethod', params).pipe(
      map((response: any) => response.data.rows.map((action: any) => action.code)),
      tap((features: string[]) => {
        if (size(features) && this.dataService.myOrgs?.length > 0) {
          this._arcliveActions = features;
        }
      }),
      catchError((error) => {
       
        return of([]);
      })
    );
  }
  // loadOrgLabels(): void {
  //   // Construct and add a label for each org to depict hierarchy
  //   Object.keys(this.data.myOrgs).forEach((key) => {
  //     const org = this.data.myOrgs[key];
  //     org.label = this.buildAncestorLabel(org);  // Assuming buildAncestorLabel() is a method in your service or component
  //   });
  
  //   // Remove orgs the user should never see (e.g., 'Public' orgs)
  //   Object.keys(this.data.myOrgs).forEach((key) => {
  //     const org = this.data.myOrgs[key];
  //     if (org.label === 'Public') {
  //       delete this.data.myOrgs[key];  // Delete the 'Public' org
  //     }
  //   });
  // }
  
  // // Assuming buildAncestorLabel is a method that constructs the label string for an org
  // private buildAncestorLabel(org: any): string {
  //   // Implement logic for building the label for the organization
  //   // This is just an example of how you might create the label
  //   return `${org.name} (Ancestor)`;
  // }
  // Implement other methods one-by-one as needed, like showBuildings, getPerimeter, etc.

  // Add new method to get building data
  getBuildings(): Observable<any> {
    return this.request('loadOrgData-findBuilding').pipe(
      map((response: any) => {
        if (response && response.data && response.data.rows) {
          return response.data.rows;
        }
        return [];
      }),
      catchError((error) => {
        console.error('Error loading buildings:', error);
        return of([]);
      })
    );
  }

  // Add new method to load buildings explicitly
  loadBuildings(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getBuildings().subscribe({
        next: (buildingsResponse) => {
          if (buildingsResponse) {
            this.data.myBuildings = {};
            for (const building of buildingsResponse) {
              const buildingEntity = new PerimeterMapEntity(
                building.id,
                building.label,
                flipLocations(building.geometry.coordinates[0]),
                building.createdAt,
                building.organizationId,
                0,
                0,
                null,
                'BUILDING',
                building.ancestors,
                '',
                this.cameraService,
                building.attributes || {},
                this.cores,
                this.cor,
                this.windowService,
                this.dataContextService,
                this.modalService,
                this.timelineService
              );
              
              this.dataContext.addMapEntity(buildingEntity);
              this.data.myBuildings[building.id] = buildingEntity;
            }
            resolve(this.data.myBuildings);
          } else {
            resolve({});
          }
        },
        error: (error) => {
          console.error('Error loading buildings:', error);
          reject(error);
        }
      });
    });
  }

  // Example of how to use the loadBuildings method
  async initializeBuildings(): Promise<void> {
    try {
      // Load buildings
      await this.loadBuildings();
      
      // You can now access the buildings through this.data.myBuildings
      // or through the data context
    } catch (error) {
      console.error('Failed to initialize buildings:', error);
    }
  }
  /**
   * Retrieves the binary data for a floorplan image.
   * @param floorplanId The ID of the floorplan to fetch.
   * @returns A promise that resolves to the binary image data (ArrayBuffer).
   */
  async getFloorplanFile(floorplanId: any): Promise<any> {
    if (!floorplanId) {
      return null;
    }

    const url = `${ConfigurationManagerService.settings.BaseURL}db/Floorplan/${floorplanId}/file`;

    try {
      return await firstValueFrom(
        this.http.get(url, { responseType: 'arraybuffer' })
      );
    } catch (error) {
      return null;
    }
  }
  

}
// function flipLocations(arg0: any): any {
//   throw new Error('Function not implemented.');
// }


function flipLocations(locations: [number, number, number?][]): number[][] {
  return locations.map(([lng, lat, alt]: [number, number, number?]) => {
    const flipped = [lat, lng];
    if (alt !== undefined) {
      flipped.push(alt);
    }
    return flipped;
  });
  
  
}



